import ih from 'immutability-helper';
import { find, initial, startsWith, without, orderBy, replace } from 'lodash';
import path from 'path';

import { nameNewFile } from 'store/utils/fileSystem';
import createFileSystemReducerWithNamedType from 'store/hoReducers/fileSystem/reducer';
import initialState from './initialState';
import { getNextPresentedFileId } from '../../utils/fileSystem';
import * as actionTypes from './actionTypes';
import { isItemPersisted, containsTemporaryFile } from './utils';

function updateDataWithNewName(state, payload) {
  const { data } = state;
  const { id, value } = payload;

  const match = find(data, ['path', id]);
  const trimmedData = without(data, match);
  const pathParts = id.split('/');
  const newPath =
    pathParts.length > 1 ? `${initial(pathParts).join('/')}/${value}` : value;

  const newEntry = {
    ...match,
    id: newPath,
    name: value,
    path: newPath,
  };

  const updateItemsWithNewPath = () =>
    trimmedData.map(item =>
      startsWith(item.path, match.path)
        ? {
            ...item,
            id: replace(item.path, match.path, newPath),
            path: replace(item.path, match.path, newPath),
          }
        : item,
    );

  const dataWithNewName =
    match.type === 'dir' ? updateItemsWithNewPath() : trimmedData;

  return [...dataWithNewName, newEntry];
}

const getUpdatedDataAfterDelete = (data, idToDelete) => {
  const { updatedOpenedFiles, updatedData } = data.reduce(
    (acc, fileOrFolder) => {
      const singleDataAreInDeletedFolder = startsWith(
        fileOrFolder.id,
        idToDelete,
      );

      const updatedData = singleDataAreInDeletedFolder
        ? acc.updatedData
        : [...acc.updatedData, fileOrFolder];

      const shouldBeOpened =
        fileOrFolder.openedAt && !singleDataAreInDeletedFolder;

      const updatedOpenedFiles = [
        ...acc.updatedOpenedFiles,
        ...(shouldBeOpened ? [fileOrFolder] : []),
      ];

      return { updatedOpenedFiles, updatedData };
    },
    { updatedOpenedFiles: [], updatedData: [] },
  );

  const updatedPresentedList = orderBy(
    updatedOpenedFiles,
    ['openedAt'],
    ['desc'],
  );
  const updatedPresentedId =
    updatedPresentedList.length > 0 ? updatedPresentedList[0].id : '';

  return { updatedData, updatedPresentedId };
};

const getUpdatedDataAfterRename = (data, { id, newName }) => {
  const match = find(data, ['id', id]);

  const trimmedData = without(data, match);
  const pathParts = match.id.split('/');
  const newPath =
    pathParts.length > 1
      ? `${initial(pathParts).join('/')}/${newName}`
      : newName;

  const newEntry = {
    ...match,
    id: newPath,
    name: newName,
    path: newPath,
  };

  return [...trimmedData, newEntry];
};

function createTemporaryItemCreationHandler(type, defaultName) {
  return (state, { payload }) => {
    const files = state.all.data;
    if (containsTemporaryFile(files)) {
      return state;
    }

    const name = nameNewFile(files, defaultName);
    const path = payload.path ? `${payload.path}/${name}` : name;

    return ih(state, {
      all: {
        data: {
          $push: [{ name, id: path, type, path, isPersisted: false }],
        },
      },
    });
  };
}

function handlePersistTemporaryItemSuccess() {
  return (state, action) => {
    const { item: persistedItem } = action.payload;
    const pathname = path.join(persistedItem.path, persistedItem.name);

    return ih(state, {
      all: {
        data: {
          $apply: items => {
            const index = items.findIndex(isItemPersisted);

            return [
              ...items.slice(0, index),
              {
                id: pathname,
                path: pathname,
                name: persistedItem.name,
                type: persistedItem.isFolder ? 'dir' : 'file',
              },
              ...items.slice(index + 1),
            ];
          },
        },
      },
    });
  };
}

function handlePersistTemporaryItemFailure() {
  return state =>
    ih(state, {
      all: {
        data: {
          $apply: items => {
            const index = items.findIndex(isItemPersisted);
            return [...items.slice(0, index), ...items.slice(index + 1)];
          },
        },
      },
    });
}

const reducerHandlers = (state = initialState, action = {}) => ({
  EXPORT_FLOW_ALL_FILES_AND_FOLDERS_REQUEST: () =>
    ih(state, {
      exportAll: {
        $merge: {
          isLoading: true,
        },
      },
    }),
  EXPORT_FLOW_ALL_FILES_AND_FOLDERS_SUCCESS: () =>
    ih(state, {
      exportAll: {
        $merge: {
          isLoading: false,
        },
      },
    }),
  EXPORT_FLOW_ALL_FILES_AND_FOLDERS_ERROR: () =>
    ih(state, {
      exportAll: {
        $merge: {
          isLoading: false,
          error: action.payload.error,
        },
      },
    }),
  ADD_FLOW_FOLDER_REQUEST: () =>
    ih(state, {
      addFolder: {
        $merge: {
          isLoading: true,
          error: '',
        },
      },
    }),
  ADD_FLOW_FOLDER_SUCCESS: () =>
    ih(state, {
      addFolder: {
        $merge: {
          isLoading: false,
        },
      },
    }),
  ADD_FLOW_FOLDER_ERROR: () =>
    ih(state, {
      addFolder: {
        $merge: {
          isLoading: false,
          error: action.payload.error,
        },
      },
    }),
  UPDATE_FLOW_FOLDER_REQUEST: () =>
    ih(state, {
      updateFolder: {
        $merge: {
          isLoading: true,
          error: '',
        },
      },
    }),
  UPDATE_FLOW_FOLDER_SUCCESS: () =>
    ih(state, {
      updateFolder: {
        $merge: {
          isLoading: false,
        },
      },
    }),
  UPDATE_FLOW_FOLDER_ERROR: () =>
    ih(state, {
      updateFolder: {
        $merge: {
          isLoading: false,
          error: action.payload.error,
        },
      },
    }),
  DELETE_FLOW_FILE_SUCCESS: () =>
    ih(state, {
      all: {
        data: {
          // Delete the file from redux store
          $apply: data => data.filter(({ id }) => id !== action.payload.id),
        },
        isLoading: {
          $set: false,
        },
      },
      deleteFile: {
        $set: {
          isLoading: false,
          error: '',
        },
      },
      show: {
        presentedId: {
          $apply: curId =>
            getNextPresentedFileId(curId, [action.payload.id], state.all.data),
        },
      },
    }),
  DELETE_FLOW_FILE_REQUEST: () =>
    ih(state, {
      deleteFile: {
        $set: {
          isLoading: true,
          error: '',
        },
      },
    }),
  DELETE_FLOW_FILE_ERROR: () =>
    ih(state, {
      deleteFile: {
        $set: {
          isLoading: false,
          error: action.payload.error,
        },
      },
    }),
  DELETE_FLOW_FOLDER_REQUEST: () =>
    ih(state, {
      deleteFolder: {
        $merge: {
          isLoading: true,
          error: '',
        },
      },
    }),
  DELETE_FLOW_FOLDER_SUCCESS: () => {
    const { updatedData, updatedPresentedId } = getUpdatedDataAfterDelete(
      state.all.data,
      action.payload.id,
    );

    return ih(state, {
      deleteFolder: { $merge: { isLoading: false } },
      all: {
        data: { $set: updatedData },
      },
      show: {
        presentedId: { $set: updatedPresentedId },
      },
    });
  },

  DELETE_FLOW_FOLDER_ERROR: () =>
    ih(state, {
      deleteFolder: {
        $merge: {
          isLoading: false,
          error: action.payload.error,
        },
      },
    }),
  EXPORT_FLOW_FILE_OR_FOLDER_REQUEST: () =>
    ih(state, {
      exportFileOrFolder: {
        $merge: {
          isLoading: true,
          error: '',
        },
      },
    }),
  EXPORT_FLOW_FILE_OR_FOLDER_SUCCESS: () =>
    ih(state, {
      exportFileOrFolder: {
        $merge: {
          isLoading: false,
        },
      },
    }),
  EXPORT_FLOW_FILE_OR_FOLDER_ERROR: () =>
    ih(state, {
      exportFileOrFolder: {
        $merge: {
          isLoading: false,
          error: action.payload.error,
        },
      },
    }),
  SYNC_FLOW_FILES_REQUEST: () =>
    ih(state, {
      syncFiles: {
        $merge: {
          isLoading: true,
          error: '',
        },
      },
    }),
  SYNC_FLOW_FILES_SUCCESS: () =>
    ih(state, {
      syncFiles: {
        $merge: {
          isLoading: false,
        },
      },
    }),
  SYNC_FLOW_FILES_ERROR: () =>
    ih(state, {
      syncFiles: {
        $merge: {
          isLoading: false,
          error: action.payload.error,
        },
      },
    }),
  RENAME_FLOW_FOLDER_REQUEST: () =>
    ih(state, {
      editFolder: {
        $merge: {
          isLoading: true,
          error: '',
        },
      },
    }),
  RENAME_FLOW_FOLDER_SUCCESS: () => {
    const { id, updatedName, updatedPath } = action.payload;
    const { data } = state.all;

    // WE NEED TO REVISIT THIS FUNCTION TO SEE IF
    // IT NEEDS TO BE REFACTORED
    // const { updatedPresentedId } = getUpdatedDataAfterDelete(data, id);
    /** ******************************* */

    const newData = updateDataWithNewName(
      { data },
      {
        id,
        value: updatedName,
      },
    );

    return ih(state, {
      editFolder: { $merge: { isLoading: false } },
      all: {
        data: { $set: newData },
        isLoading: {
          $set: false,
        },
      },
      show: {
        presentedId: {
          $apply: currId => (currId ? currId.replace(id, updatedPath) : null),
        },
      },
    });
  },
  RENAME_FLOW_FOLDER_ERROR: () =>
    ih(state, {
      editFolder: {
        $set: {
          isLoading: false,
          error: action.payload.error,
        },
      },
    }),
  RENAME_FLOW_FILE_REQUEST: () =>
    ih(state, {
      editFile: {
        $merge: {
          isLoading: true,
          error: '',
        },
      },
    }),
  RENAME_FLOW_FILE_SUCCESS: () => {
    const { id, newName } = action.payload;

    return ih(state, {
      all: {
        data: {
          $set: [...getUpdatedDataAfterRename(state.all.data, { id, newName })],
        },
      },
      show: {
        presentedId: {
          $apply: curId => getNextPresentedFileId(curId, [id], state.all.data),
        },
      },
      editFile: {
        $merge: {
          isLoading: false,
        },
      },
    });
  },
  RENAME_FLOW_FILE_ERROR: () =>
    ih(state, {
      editFile: {
        $merge: {
          isLoading: false,
          error: action.payload.error,
        },
      },
    }),
  UPLOAD_FLOW_FILES_ERROR: () =>
    ih(state, {
      upload: {
        $merge: {
          isLoading: false,
          error: action.payload.error,
          conflicts: action.payload.conflicts,
        },
      },
    }),
  GET_FLOW_FILE_REQUEST: () =>
    ih(state, {
      show: {
        $merge: {
          isLoading: true,
          presentedId: action.payload.isOpened
            ? state.show.presentedId
            : action.payload.id,
        },
      },
    }),
  UPLOAD_FLOW_FILES_REQUEST: () =>
    ih(state, {
      upload: {
        $merge: {
          isLoading: true,
          error: '',
        },
      },
    }),
  [actionTypes.CREATE_TEMP_FILE]: createTemporaryItemCreationHandler(
    'file',
    'new-file',
  ),
  [actionTypes.CREATE_TEMP_FOLDER]: createTemporaryItemCreationHandler(
    'dir',
    'new-folder',
  ),
  [actionTypes.PERSIST_TEMPORARY_ITEM_SUCCESS]: handlePersistTemporaryItemSuccess(),
  [actionTypes.PERSIST_TEMPORARY_ITEM_FAILURE]: handlePersistTemporaryItemFailure(),
});

export default createFileSystemReducerWithNamedType(
  'FLOW',
  initialState,
  reducerHandlers,
);
